import EditIcon from "@material-ui/icons/Edit";
import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import ShowLocationsDialog from "../../components/maps/ShowLocationsDialog";
import strings from "../../config/localization";
import {
  useEditLocationMutation,
  useGetAllLocationsQuery,
} from "../../services/appln";
import { getDefaultLocation, getDefaultZoom } from "../constants";
import {
  getLocationStrMap,
  getParentLocationType,
  getReverseLocationStrMap,
} from "../util";

interface FormData {
  name: string;
  address: string;
  parentId: string;
  kind: string;
  latitude: string;
  longitude: string;
}

interface Props {
  bandobastId: string;
  timeslotId: string;
  id: string;
  initialValues: any;
}

export default function EditLocation(props: Props) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [selectLocationDialogState, setSelectLocationDialogState] =
    React.useState(false);

  const [selectedLocation, setSelectedLocation] = React.useState(null);

  const [editLocation, editLocationParams] = useEditLocationMutation();

  const [locationByKindMap, setLocationsByKindMap] = React.useState<
    Map<string, any>
  >(new Map());

  const iv: FormData = {
    name: props.initialValues.name,
    parentId: props.initialValues.parent,
    address: props.initialValues.address,
    kind: getLocationStrMap().get(props.initialValues.kind)!,
    latitude: "",
    longitude: "",
  };

  const locationData = useGetAllLocationsQuery({
    bandobastId: props.bandobastId,
    timeslotId: props.timeslotId,
  });

  React.useEffect(() => {
    const locationMap = getLocationStrMap();
    const locationByKindMap = new Map<string, any[]>();

    if (locationData.isSuccess) {
      const data = locationData.data.data;
      data.forEach((location: any) => {
        const kind = locationMap.get(location.kind)!;
        if (!locationByKindMap.has(kind)) {
          locationByKindMap.set(kind, []);
        }
        locationByKindMap.get(kind)?.push(location);
      });

      setLocationsByKindMap(locationByKindMap);
      console.log("EditLocation, locationByKindMap:", locationByKindMap);
    }
  }, [locationData]);

  // TODO: there could be duplicate location names
  const locationMap = new Map<string, string>();
  locationData.data?.data.forEach((element: any) => {
    locationMap.set(element.name, element.id);
  });

  console.log("EditLocation, editLocationParams:", editLocationParams);
  React.useEffect(() => {
    if (editLocationParams.isSuccess) {
      handleClose();
    }
  }, [editLocationParams]);

  const getParents = (kind: string) => {
    if (!kind || kind === strings.range) {
      return [];
    }
    const parentType = getParentLocationType(kind);
    return locationByKindMap
      .get(parentType)
      .map((e: any) => e.name)
      .toSorted();
  };

  return (
    <div>
      <IconButton color="secondary" onClick={handleClickOpen}>
        <EditIcon />
      </IconButton>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {strings.editLocation}
        </DialogTitle>
        {editLocationParams.isError && (
          <ErrorMsg msg={strings.failedToEditLocation} />
        )}
        {editLocationParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <ShowLocationsDialog
            state={selectLocationDialogState}
            data={[]}
            onClose={() => {}}
            onSelect={(location: any) => {
              setSelectedLocation(location);
              setSelectLocationDialogState(false);
            }}
            defaultZoom={getDefaultZoom()}
            defaultCenter={getDefaultLocation()}
          />
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              if (!values.name) {
                errors.name = strings.nameMandatory;
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              const valuesClone = { ...values };
              valuesClone.parentId = locationMap.get(values.parentId!)!;
              valuesClone.kind = getReverseLocationStrMap().get(values.kind!)!;
              if (selectedLocation) {
                valuesClone.latitude = selectedLocation["lat"];
                valuesClone.longitude = selectedLocation["lng"];
              }
              console.log(
                "EditLocation, values after modification",
                valuesClone,
              );
              editLocation({
                bandobastId: props.bandobastId,
                id: props.id,
                body: valuesClone,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        disabled
                        id="kind"
                        value={values.kind}
                        options={[]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={strings.kind}
                          />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("kind", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="address"
                        name="address"
                        label={strings.address}
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="parentId"
                        value={values.parentId}
                        options={getParents(values.kind)}
                        renderInput={(params) => (
                          <TextField {...params} label={strings.parent} />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("parentId", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectLocationDialogState(true);
                        }}
                      >
                        {strings.selectLocation}
                      </Button>
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.save}
                        </Button>
                        <Button variant="contained" onClick={handleClose}>
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
